import { APP_SETTINGS } from "../shared/app_settings";
import { CHANNELS } from "../shared/channels";
import { NoteEntry } from "../shared/types";
import { call } from "./bridge";
import { byId } from "./dom";
import { wireDictateButton } from "./dictation";
import { locale, t } from "./i18n";
import { showView } from "./navigation";
import { applyStickyColor } from "./views/sticky_note";
import { debounce } from "./utils";

// a small note editor that opens over home, so a sticky can be read, typed into, dictated into or thrown away without leaving

let openName = "";
let changed: () => void = () => undefined;

const nameInput = () => byId<HTMLInputElement>("note-sheet-name");
const bodyInput = () => byId<HTMLTextAreaElement>("note-sheet-body");

async function save(): Promise<void> {
  if (!openName && !bodyInput().value.trim() && !nameInput().value.trim()) return;
  const name = nameInput().value.trim() || t("untitled");
  const saved = await call<NoteEntry>(CHANNELS.notesSave, name, bodyInput().value);
  if (openName && openName !== saved.name) await call(CHANNELS.notesRemove, openName);
  openName = saved.name;
  applyStickyColor(byId("note-sheet-card"), openName);
  byId("note-sheet-status").textContent = t("saved {time}", { time: new Date().toLocaleTimeString(locale()) });
}

const autosave = debounce(() => void save(), APP_SETTINGS.noteAutosaveMs);

async function close(): Promise<void> {
  if (byId("note-sheet").hidden) return;
  await save();
  byId("note-sheet").hidden = true;
  changed();
}

export function openNoteSheet(note: NoteEntry | null): void {
  openName = note?.name ?? "";
  nameInput().value = note?.name ?? t("sticky {date}", { date: new Date().toLocaleDateString(locale()) });
  bodyInput().value = note?.content ?? "";
  applyStickyColor(byId("note-sheet-card"), nameInput().value);
  byId("note-sheet-status").textContent = "";
  byId("note-sheet").hidden = false;
  bodyInput().focus();
}

// onChange runs after the sheet closes so home can redraw its stickies
export function setupNoteSheet(onChange: () => void): void {
  changed = onChange;
  byId("note-sheet-close").addEventListener("click", () => void close());
  byId("note-sheet").addEventListener("click", (event) => {
    if (event.target === byId("note-sheet")) void close();
  });
  nameInput().addEventListener("input", autosave);
  bodyInput().addEventListener("input", autosave);
  wireDictateButton(byId<HTMLButtonElement>("note-sheet-mic"), bodyInput(), (text) => {
    byId("note-sheet-status").textContent = text ? `${text}…` : "";
  });
  byId("note-sheet-delete").addEventListener("click", async () => {
    if (openName && !confirm(t("delete {name}?", { name: openName }))) return;
    if (openName) await call(CHANNELS.notesRemove, openName);
    openName = "";
    byId("note-sheet").hidden = true;
    changed();
  });
  byId("note-sheet-open").addEventListener("click", async () => {
    await save();
    const name = openName;
    byId("note-sheet").hidden = true;
    changed();
    showView("notes", name);
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !byId("note-sheet").hidden) void close();
  });
}
